import type { Priority } from '@/types';
import type { ReactNode } from 'react';
import { CircleDashed, Flag, Zap, ShieldAlert, Sparkles, Tag, Archive, Clock } from 'lucide-react';
import { getInitials, getAvatarColor } from './formatters';
import { TASK_STATUS } from '@/constants/taskStatuses';
import type { TaskStatusDef } from '@/db/taskStatuses';
import clsx from 'clsx';

export interface FilterOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

/**
* Ordine coerente per gli status (valori canonici)
*/
const STATUS_ORDER: Record<string, number> = {
  [TASK_STATUS.IN_PROGRESS]: 1,
  [TASK_STATUS.OPEN]: 2,
  [TASK_STATUS.BLOCKED]: 3,
  [TASK_STATUS.COMPLETED]: 4,
  [TASK_STATUS.CANCELLED]: 5,
};

/**
* Etichette italiane per gli status (valori canonici)
*/
const STATUS_LABELS: Record<string, string> = {
  [TASK_STATUS.IN_PROGRESS]: 'In corso',
  [TASK_STATUS.OPEN]: 'Da fare',
  [TASK_STATUS.BLOCKED]: 'Bloccato',
  [TASK_STATUS.COMPLETED]: 'Completato',
  [TASK_STATUS.CANCELLED]: 'Annullato',
};

/**
* Icone per gli status (valori canonici)
*/
const STATUS_ICONS: Record<string, ReactNode> = {
  [TASK_STATUS.IN_PROGRESS]: <Zap className="w-3 h-3 text-accent-blue" />,
  [TASK_STATUS.OPEN]: <CircleDashed className="w-3 h-3 text-text-quaternary" />,
  [TASK_STATUS.BLOCKED]: <ShieldAlert className="w-3 h-3 text-accent-rose" />,
  [TASK_STATUS.COMPLETED]: <Sparkles className="w-3 h-3 text-accent-emerald" />,
  [TASK_STATUS.CANCELLED]: <CircleDashed className="w-3 h-3 text-text-quaternary" />,
};

/**
* Ordine coerente per le priorità
*/
const PRIORITY_ORDER: Record<string, number> = {
  'urgent': 1,
  'high': 2,
  'normal': 3,
  'low': 4,
};

/**
* Etichette italiane per le priorità
*/
const PRIORITY_LABELS: Record<string, string> = {
  'urgent': 'Urgente',
  'high': 'Alta',
  'normal': 'Normale',
  'low': 'Bassa',
};

/** Map icon name string → Lucide component for custom statuses */
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  CircleDashed, Zap, ShieldAlert, Sparkles, Archive, Clock, Flag,
};

/** Map color token → Tailwind class for custom statuses */
function resolveColorClass(color?: string | null): string {
  if (!color) return 'text-text-quaternary';
  // Already a Tailwind class (e.g. "accent-blue" → "text-accent-blue")
  if (color.startsWith('text-')) return color;
  if (color.startsWith('accent-')) return `text-${color}`;
  return 'text-text-quaternary';
}

/**
* Genera opzioni di filtro per gli status.
* Se customStatuses è fornito e non vuoto, usa quelli al posto dei canonici.
*/
export function getStatusFilterOptions(statuses: string[], customStatuses?: TaskStatusDef[]): FilterOption[] {
  if (customStatuses && customStatuses.length > 0) {
    // Build lookup from custom definitions
    const defMap = new Map(customStatuses.map(s => [s.value, s]));
    // Show only custom statuses when defined (no global leakage)
    return Array.from(defMap.keys()).sort((a, b) => {
      const orderA = defMap.get(a)?.order_index ?? 99;
      const orderB = defMap.get(b)?.order_index ?? 99;
      return orderA - orderB;
    }).map(value => {
      const def = defMap.get(value);
      const IconComp = def?.icon ? ICON_MAP[def.icon] || CircleDashed : CircleDashed;
      const colorCls = resolveColorClass(def?.color);
      return {
        value,
        label: def?.name || STATUS_LABELS[value] || value,
        icon: <IconComp className={clsx('w-3 h-3', colorCls)} />,
      };
    });
  }

  // Fallback: canonical statuses
  return Array.from(new Set(statuses))
  .filter(Boolean)
  .sort((a, b) => {
      const orderA = STATUS_ORDER[a] ?? 99;
      const orderB = STATUS_ORDER[b] ?? 99;
      return orderA - orderB;
  })
  .map(status => ({
    value: status,
    label: STATUS_LABELS[status] || status,
    icon: STATUS_ICONS[status] || <CircleDashed className="w-3 h-3 text-text-quaternary" />,
  }));
}

/**
* Colori delle priorità coerenti con il resto dell'applicazione
*/
const PRIORITY_COLORS: Record<string, string> = {
  'urgent': 'text-accent-rose',
  'high': 'text-accent-orange',
  'normal': 'text-accent-amber',
  'low': 'text-accent-cyan',
};

/**
* Genera opzioni di filtro per le priorità con ordine coerente
*/
export function getPriorityFilterOptions(priorities: (Priority | null)[]): FilterOption[] {
  return priorities
  .filter((priority): priority is NonNullable<Priority> => priority !== null)
  .sort((a, b) => {
      const orderA = PRIORITY_ORDER[a] ?? 99;
      const orderB = PRIORITY_ORDER[b] ?? 99;
      return orderA - orderB;
  })
  .map(priority => ({
      value: priority,
      label: PRIORITY_LABELS[priority] || priority,
      icon: <Flag className={clsx('w-3 h-3', PRIORITY_COLORS[priority] || 'text-text-quaternary')} />,
  }));
}

/**
* Genera opzioni di filtro per gli assegnatari con ordine alfabetico e avatar
*/
export function getAssigneeFilterOptions(assignees: string[], resolveDisplayName?: (id: string) => string): FilterOption[] {
  return Array.from(new Set(assignees))
  .filter(Boolean)
  .sort((a, b) => {
      const nameA = resolveDisplayName ? resolveDisplayName(a) : a;
      const nameB = resolveDisplayName ? resolveDisplayName(b) : b;
      return nameA.localeCompare(nameB, 'it', { sensitivity: 'base' });
  })
  .map(assignee => {
      const displayName = resolveDisplayName ? resolveDisplayName(assignee) : assignee;
      return {
        value: assignee,
        label: displayName,
        icon: (
          <div className={clsx(
            'w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-semibold text-white shrink-0',
            getAvatarColor(displayName)
          )}>
            {getInitials(displayName)}
          </div>
        ),
      };
  });
}

/**
* Genera opzioni di filtro per i tag con ordine alfabetico e icona
*/
export function getTagFilterOptions(tags: string[]): FilterOption[] {
  return Array.from(new Set(tags))
  .filter(Boolean)
  .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
  .map(tag => ({
      value: tag,
      label: tag,
      icon: <Tag className="w-3 h-3 text-accent-violet" />,
  }));
}

/**
* Genera tutte le opzioni di filtro con ordine coerente
*/
export function getAllFilterOptions(
  statuses: string[],
  assignees: string[],
  tags: string[],
  priorities: (Priority | null)[],
  resolveDisplayName?: (id: string) => string,
  customStatuses?: TaskStatusDef[],
): {
  status: FilterOption[];
  assignees: FilterOption[];
  tags: FilterOption[];
  priority: FilterOption[];
} {
  return {
  status: getStatusFilterOptions(statuses, customStatuses),
  assignees: getAssigneeFilterOptions(assignees, resolveDisplayName),
  tags: getTagFilterOptions(tags),
  priority: getPriorityFilterOptions(priorities),
  };
}
